import React, { FC, useCallback, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import { Layout, message } from 'antd';
import AssetForm from '../asset_form/AssetForm';
import { assetsRestCrudThunksForBuyer } from '../../thunks/assets_thunks';
import { getAssetCopy } from '../../utils/assetUtils';
import { getLinkWIthBackLinkParams, getLocationSearch } from '../../utils/HistoryUtils';
import { EditPage } from '../edit_page/EditPage';
import { getEntityById, nullSafeGet } from '../../utils/DataAccessUtils';

const { Content } = Layout;
const queryString = require('qs');

const NewAssetPage: FC<any> = ({ history, location, getAsset }): React.ReactElement => {
	const [assetFetching, setAssetFetching] = useState(false);
	const [assetData, setAssetData] = useState({});

	// Merge order-independently with whatever the parent fetch may have already
	// written: copied fields are the base, parent-derived fields (parentId,
	// locationId, etc.) win on conflict regardless of which fetch resolves last.
	const copyAssetData = (asset) => setAssetData((prev) => ({ ...getAssetCopy(asset), ...prev }));

	const getAssetData = useCallback(
		(id) => {
			setAssetFetching(true);
			getAsset(id)
				.then(copyAssetData)
				.catch((err) => message.error(err))
				.finally(() => setAssetFetching(false));
		},
		[getAsset]
	);

	const getParentAssetData = useCallback(
		(parentId) => {
			setAssetFetching(true);
			getAsset(parentId)
				.then((parent) =>
					setAssetData((prev) => ({
						...prev,
						parentId: parseInt(parentId, 10),
						locationId: nullSafeGet('locationId', parent),
						areaId: nullSafeGet('areaId', parent),
						images: nullSafeGet('images', parent),
						manufacturer: nullSafeGet('manufacturer', parent),
						modelNumber: nullSafeGet('modelNumber', parent),
						serialNumber: nullSafeGet('serialNumber', parent),
						assetTypeId: nullSafeGet('assetTypeId', parent),
						assetModelId: nullSafeGet('assetModelId', parent),
						assetTypeDetails: nullSafeGet('assetTypeDetails', parent),
						// The Is Leased field is hidden for sub assets, but the value is
						// still inherited from the parent and submitted as-is.
						isLeased: nullSafeGet('isLeased', parent),
					}))
				)
				.catch((err) => message.error(err))
				.finally(() => setAssetFetching(false));
		},
		[getAsset]
	);

	useEffect(() => {
		const queryParams = queryString.parse(getLocationSearch(location));
		queryParams.copyFromAssetId && getAssetData(queryParams.copyFromAssetId);
		queryParams.parentId && getParentAssetData(queryParams.parentId);
	}, [getAssetData, getParentAssetData, location]);

	const isSubAsset = !!nullSafeGet('parentId', assetData);

	return (
		<Content>
			<EditPage
				title={isSubAsset ? 'Create Sub Asset' : 'Create Asset'}
				formComponentProps={{
					formData: assetData,
					onSuccess: (rec) => {
						// Use the explicit /details tab: the bare /buyer/assets/detail/:id
						// route redirects to /details and drops the query string, which would
						// strip the backlinkParams we attach below.
						const newAssetDetailUrl = `/buyer/assets/detail/${rec.id}/details`;
						const queryParams = queryString.parse(getLocationSearch(location));
						const parentId = nullSafeGet('parentId', assetData);
						if (queryParams.backlinkParams) {
							// The originating page forwarded its back-link chain (e.g. parent
							// asset -> assets index with filters). Carry it onto the new asset's
							// detail page so its back button walks the full path back.
							history.push(
								`${newAssetDetailUrl}?backlinkParams=${encodeURIComponent(
									queryParams.backlinkParams
								)}`
							);
						} else if (parentId) {
							// Fallback for sub asset entry points that didn't forward a chain:
							// at least point the back button at the parent asset.
							history.push(
								getLinkWIthBackLinkParams(
									{ pathname: `/buyer/assets/detail/${parentId}/details`, search: '' },
									'Back to Asset',
									newAssetDetailUrl
								)
							);
						} else {
							history.push(newAssetDetailUrl);
						}
					},
				}}
				fetching={assetFetching}
				FormComponent={AssetForm}
			/>
		</Content>
	);
};

const mapStateToProps = (state, ownProps) => ({
	history: ownProps.history,
	asset: getEntityById(ownProps.match.params.id, state.assets),
});

const mapDispatchToProps = (dispatch) => ({
	getAsset: (id) => dispatch(assetsRestCrudThunksForBuyer.readOne(id)),
});

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(NewAssetPage));
